import { useCallback } from 'react';

import {
  useErpSurveyQuestionTagsQuery,
  useErpSurveyUntaggedCountQuery,
  useSaveQuestionMetricsMutation,
} from 'core/graphql';

export interface ISurveyQuestionTagRow {
  questionId: string;
  surveyId: string;
  surveyTitle: string;
  courseName?: string | null;
  number: number;
  text: string;
  questionType: string;
  metric: string;
  /** Keyword guess for an untagged question; '' when the server is unsure. */
  suggestedMetric: string;
}

/**
 * Spec 0038 revision — the bulk tagging screen.
 *
 * The analytics are only as good as the tagging, and on real data almost
 * nothing was tagged: the topic dimensions answered nothing and said nothing
 * about why. Tagging inside each survey's constructor is work nobody does, so
 * this is one list across every survey.
 */
export const useSurveyQuestionTags = (filters: {
  onlyUntagged?: boolean;
  courseId?: string;
  search?: string;
}) => {
  const { data, loading, error, refetch } = useErpSurveyQuestionTagsQuery({
    variables: {
      onlyUntagged: filters.onlyUntagged,
      courseId: filters.courseId || undefined,
      search: filters.search || undefined,
    },
    fetchPolicy: 'cache-and-network',
  });

  return {
    rows: (data?.surveyQuestionTags || []) as ISurveyQuestionTagRow[],
    loading,
    denied: Boolean(error),
    refetch,
  };
};

/** The «N вопросов без темы» prompt. Readable by everyone who sees analytics. */
export const useUntaggedQuestionCount = (): number => {
  const { data } = useErpSurveyUntaggedCountQuery({
    fetchPolicy: 'cache-and-network',
  });
  return data?.surveyUntaggedCount ?? 0;
};

export const useSaveQuestionMetrics = () => {
  const [mutate, { loading }] = useSaveQuestionMetricsMutation({
    // Operation NAMES. The tagging screen also refetches through its own
    // `refetch`, so a rename here degrades to a stale counter rather than a
    // stale table.
    refetchQueries: ['ErpSurveyQuestionTags', 'ErpSurveyUntaggedCount'],
  });

  const save = useCallback(
    async (items: { questionId: string; metric: string }[]) => {
      const { data } = await mutate({ variables: { items } });
      const payload = data?.setQuestionMetrics;
      return {
        success: Boolean(payload?.success),
        changedCount: payload?.changedCount || 0,
        errorCode: payload?.customErrors?.[0]?.code || undefined,
      };
    },
    [mutate],
  );

  return { save, loading };
};
